import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import { useLocation } from "@tanstack/react-router"
import { twJoin } from "tailwind-merge"
import { Link } from "@/src/components/core/components/links/Link"
import type { ProjectsForCurrentUser } from "@/src/server/projects/types"
import { NavigationAdminMenu } from "../NavigationAdminMenu"
import { ProjectsSwitch } from "../NavigationLoggedIn/ProjectsSwitch"
import { shouldHighlight, useMenuItems } from "../NavigationLoggedInProject/useMenuItems"
import { NavigationGeneralLogo } from "../NavigationLoggedOut/TrassenscoutLogo"
import { NavigationMetaMenu } from "../NavigationMetaMenu"
import { NavigationUser } from "../NavigationUser/NavigationUser"
import { NavigationUserLoggedOut } from "../NavigationUser/NavigationUserLoggedOut"
import { NavigationUserPublic } from "../NavigationUser/NavigationUserPublic"
import type { NavigationUserVariant } from "../types"

type Props = {
  homeLink: string
  homeLinkText: string
  menuItems?: ReturnType<typeof useMenuItems>
  projects?: ProjectsForCurrentUser
  /** Round icon actions, left of the admin, meta, and user menus. */
  actions?: React.ReactNode
  userVariant?: NavigationUserVariant
}

export const NavigationMobile = ({
  homeLink,
  homeLinkText,
  menuItems,
  projects,
  actions,
  userVariant = "auto",
}: Props) => {
  const pathname = useLocation().pathname

  return (
    <Disclosure as="div" className="relative flex flex-col nav:hidden">
      {({ open }) => (
        <>
          <div className="relative flex min-h-16 items-center justify-between nav:h-16">
            <div className="absolute inset-y-0 right-0 flex items-center space-x-2">
              {projects && <ProjectsSwitch projects={projects} />}
              {actions}

              {userVariant === "loggedOut" ? (
                <NavigationUserLoggedOut />
              ) : userVariant === "public" ? (
                <NavigationUserPublic />
              ) : (
                <>
                  <NavigationAdminMenu />
                  <NavigationMetaMenu />
                  <NavigationUser />
                </>
              )}

              {Boolean(menuItems?.length) && (
                <DisclosureButton className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:ring-2 focus:ring-white focus:outline-hidden focus:ring-inset">
                  <span className="sr-only">Hauptmenü öffnen</span>
                  {open ? (
                    <XMarkIcon className="block size-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block size-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              )}
            </div>
            <div className="flex flex-1 items-center justify-start nav:items-stretch">
              <div className="flex shrink-0 items-center">
                {pathname === homeLink ? (
                  <NavigationGeneralLogo beta={false} />
                ) : (
                  <Link to={homeLink}>
                    <NavigationGeneralLogo beta={false} />
                    <span className="sr-only">{homeLinkText}</span>
                  </Link>
                )}
              </div>
            </div>
          </div>

          <DisclosurePanel className="divide-y-2 divide-gray-900">
            <div className="space-y-3 pt-2 pb-3">
              {menuItems?.map((item) => {
                const current = shouldHighlight(item, pathname)

                return (
                  <DisclosureButton key={item.name} as="div">
                    <Link
                      to={item.href}
                      classNameOverwrites={twJoin(
                        current
                          ? "bg-gray-900 text-white"
                          : "text-gray-300 hover:bg-gray-700 hover:text-white",
                        "block rounded-md px-3 py-2 text-sm font-medium",
                      )}
                    >
                      {item.name}
                    </Link>
                  </DisclosureButton>
                )
              })}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}
